import styled from 'styled-components';
import { ErrorBoundary } from '../components/ui';

const Page = styled.div`
  min-height: 100%;
  padding: clamp(2rem, 5vw, 4.5rem) 0;
`;

const Header = styled.header`
  max-width: 48rem;
  margin: 0 auto clamp(2rem, 5vw, 4rem);
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 0.8rem;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
  font-size: clamp(2.4rem, 6vw, 4.5rem);
  line-height: 1.05;
`;

const Intro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: 1.05rem;
  line-height: 1.7;
`;

const SectionGrid = styled.div`
  display: grid;
  gap: clamp(1rem, 3vw, 1.75rem);
  max-width: 74rem;
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Section = styled.section`
  min-width: 0;
  padding: clamp(1.25rem, 3vw, 2rem);
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
`;

const WideSection = styled(Section)`
  grid-column: 1 / -1;
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: clamp(1.35rem, 3vw, 1.8rem);
`;

const Body = styled.div`
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  line-height: 1.7;

  p {
    margin: 0 0 1rem;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const FactList = styled.ul`
  display: grid;
  gap: 0.8rem;
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;
`;

const Fact = styled.li`
  padding: 0.9rem 1rem;
  border-left: 3px solid ${({ theme }) => theme.colors.gallery.accent.primary};
  background: ${({ theme }) => theme.colors.gallery.surface};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  line-height: 1.6;
`;

const Strong = styled.strong`
  color: ${({ theme }) => theme.colors.gallery.text.primary};
`;

const Map = styled.div`
  overflow: hidden;
  margin-bottom: 1.25rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: 10px;

  iframe {
    display: block;
    width: 100%;
    min-height: 16rem;
    border: 0;
  }
`;

const MapLink = styled.a`
  display: inline-block;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.gallery.accent.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const ContactList = styled.dl`
  display: grid;
  gap: 1rem;
  margin: 0;
`;

const ContactItem = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const ContactLabel = styled.dt`
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const ContactValue = styled.dd`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  line-height: 1.6;
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.gallery.accent.primary};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const ClassList = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const ClassCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.gallery.surface};
`;

const ClassName = styled.h3`
  margin: 0 0 0.25rem;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-size: 1rem;
`;

const ClassDuration = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: 0.9rem;
`;

const Price = styled.span`
  color: ${({ theme }) => theme.colors.gallery.accent.primary};
  font-size: 1.35rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  white-space: nowrap;
`;

const PolicyList = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Policy = styled.div`
  padding: 1rem;
  border-left: 3px solid ${({ theme }) => theme.colors.gallery.accent.primary};
  background: ${({ theme }) => theme.colors.gallery.surface};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  line-height: 1.65;

  p {
    margin: 0 0 0.65rem;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const classes = [
  { name: 'Drawing Class', duration: '1 hour group class', price: '$30' },
  { name: 'Painting Class', duration: '1½ hours group class', price: '$42' },
  { name: 'Private Class', duration: '1 hour private class', price: '$70' },
];

export function AboutPage() {
  return (
    <ErrorBoundary>
      <Page>
        <div className="container">
          <Header>
            <Title>Joanne Art Class</Title>
            <Intro>
              Art classes for all ages in historic Chadds Ford, Pennsylvania.
              Instruction includes drawing and painting for students at every level.
            </Intro>
          </Header>

          <SectionGrid>
            <WideSection>
              <SectionTitle>Meet the instructor</SectionTitle>
              <Body>
                <p>
                  Our instructor has taught in the Chadds Ford area for <Strong>26 years</Strong> and is a
                  <Strong> professionally trained painter</Strong>. Students have received recognition
                  at school exhibitions, community art shows, and nationwide competitions.
                </p>
                <p>
                  Classes welcome students ages <Strong>6 to 86</Strong>. The majority of students have
                  been with the class for <Strong>over 5 years</Strong>.
                </p>
                <FactList>
                  <Fact>
                    Instruction includes <Strong>pencil sketching, soft pastel, acrylic, and oil painting</Strong>.
                  </Fact>
                </FactList>
              </Body>
            </WideSection>

            <Section>
              <SectionTitle>Visit the studio</SectionTitle>
              <Map>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6123.534557677668!2d-75.551886022826!3d39.87945048816309!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6faa2ea56adf5%3A0x42132bc22e62568d!2s1204%20Baltimore%20Pike%2C%20Chadds%20Ford%2C%20PA%2019317!5e0!3m2!1sen!2sus!4v1756495651104!5m2!1sen!2sus"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Joanne Art Class Location"
                />
              </Map>
              <MapLink
                href="https://www.google.com/maps/search/?api=1&query=1204%20Baltimore%20Pike%20Suite%20306%20Chadds%20Ford%20PA%2019317"
                target="_blank"
                rel="noreferrer"
              >
                Open map
              </MapLink>
              <ContactList>
                <ContactItem>
                  <ContactLabel>Address</ContactLabel>
                  <ContactValue>1204 Baltimore Pike, Suite #306, Chadds Ford, PA 19317</ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Phone</ContactLabel>
                  <ContactValue><Link href="tel:+16103065106">(610) 306-5106</Link></ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue><Link href="mailto:joanneartclass@yahoo.com">joanneartclass@yahoo.com</Link></ContactValue>
                </ContactItem>
              </ContactList>
            </Section>

            <Section>
              <SectionTitle>Class times</SectionTitle>
              <FactList>
                <Fact><Strong>Monday:</Strong> 5:30-7:00 PM, 7:00-8:30 PM</Fact>
                <Fact><Strong>Tuesday:</Strong> 6:00-7:00 PM, 7:00-8:30 PM</Fact>
                <Fact><Strong>Wednesday:</Strong> 5:00-6:00 PM, 6:00-7:30 PM</Fact>
                <Fact><Strong>Thursday:</Strong> 4:30-6:00 PM, 6:00-7:30 PM</Fact>
                <Fact><Strong>Friday:</Strong> 4:30-6:00 PM, 6:00-7:30 PM</Fact>
              </FactList>
            </Section>

            <Section>
              <SectionTitle>Classes and pricing</SectionTitle>
              <ClassList>
                {classes.map(classInfo => (
                  <ClassCard key={classInfo.name}>
                    <div>
                      <ClassName>{classInfo.name}</ClassName>
                      <ClassDuration>{classInfo.duration}</ClassDuration>
                    </div>
                    <Price>{classInfo.price}</Price>
                  </ClassCard>
                ))}
              </ClassList>
              <Body>
                <p>Payment is due before the first class of each month. A $5 late fee applies after 7 days.</p>
              </Body>
            </Section>

            <Section>
              <SectionTitle>Policies</SectionTitle>
              <PolicyList>
                <Policy>
                  <p><Strong>Teacher cancellations:</Strong> Classes cancelled by the teacher will be reimbursed.</p>
                  <p><Strong>Student absences:</Strong> No reimbursement for student absences.</p>
                  <p><Strong>Rescheduling:</Strong> Requires 24-hour advance notice. Must be completed within 3 months.</p>
                  <p><Strong>Annual Limit:</Strong> Maximum 3 reschedules per year.</p>
                  <p>No credit or refund for missed classes.</p>
                </Policy>
              </PolicyList>
            </Section>
          </SectionGrid>
        </div>
      </Page>
    </ErrorBoundary>
  );
}
